import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { releaseAsymmetricMarginGpu } from '~/engine/util/algorithms/sdf/AsymmetricMarginGL';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { Environment } from '~/engine/data/Environment';
import { getErrorMessage } from '~/utils/errors';
import { waitForUI } from '~/utils/uiUtils';
import { MAX_ASYMMETRIC_MARGIN_MM } from './AsymmetricMarginControls';
import { getPreviewColor } from './marginToolbarUtils';
import { usePreviewBase } from '../shared/usePreviewBase';

interface UseMarginPreviewOptions {
    environment: Environment | null;
    inputRoi: RegionOfInterest | undefined;
}

const keyForSymmetric = (marginValueMm: number): string =>
    `sym:${marginValueMm}`;
// Identity (all-margins-zero) preview: never consumed by apply, it only
// exists to "set the stage" (dim others, highlight the source).
const KEY_IDENTITY = 'identity';
const keyForAsymmetric = (
    negLpsMm: ReadonlyVec3,
    posLpsMm: ReadonlyVec3
): string =>
    `asym:${negLpsMm[0]},${negLpsMm[1]},${negLpsMm[2]}` +
    `|${posLpsMm[0]},${posLpsMm[1]},${posLpsMm[2]}`;

export function useMarginPreview({
    environment,
    inputRoi,
}: UseMarginPreviewOptions) {
    const {
        isPreviewing,
        setIsPreviewing,
        updateView,
        beginOperation,
        setResult,
        consumeResult,
        abortInFlight,
        cleanup,
    } = usePreviewBase({ environment });

    // The padded "expanded" SDF that the asymmetric passes read from.
    // It depends only on the source texture, so it is built once per ROI
    // and reused across every preview and apply, then disposed when the
    // source changes or the tool closes. Shared with the apply hook via
    // getExpanded so both read the same texture.
    const expandedRef = useRef<ContourTexture | null>(null);

    useEffect(() => {
        return () => {
            if (expandedRef.current) {
                expandedRef.current.delete();
                expandedRef.current = null;
            }
            // The GPU mipmaps are keyed on the expanded SDF's texture
            // handle, so freeing them here releases that memory and lets
            // the next source rebuild against a fresh expanded.
            releaseAsymmetricMarginGpu();
        };
    }, [inputRoi?.texture]);

    const getExpanded = useCallback((): ContourTexture | undefined => {
        const src = inputRoi?.texture;
        if (!src) {
            return undefined;
        }
        if (!expandedRef.current) {
            expandedRef.current = SDFOperations.createAsymmetricExpanded(
                src,
                MAX_ASYMMETRIC_MARGIN_MM
            );
        }
        return expandedRef.current;
    }, [inputRoi]);

    const handlePreviewSymmetric = useCallback(
        async (marginValue: number) => {
            if (
                !environment?.structureSet ||
                !inputRoi?.texture ||
                !environment.image
            ) {
                return;
            }

            if (marginValue === 0) {
                cleanup();
                setIsPreviewing(false);
                return;
            }

            const signal = beginOperation();
            setIsPreviewing(true);

            try {
                const marginTexture = await SDFOperations.applySymmetricMargin(
                    inputRoi.texture,
                    marginValue
                );

                if (signal.aborted) {
                    marginTexture.delete();
                    return;
                }

                const previewObj = new CustomTargetObject(
                    environment.image,
                    marginTexture,
                    getPreviewColor(inputRoi.color)
                );
                previewObj.renderOptions = { fillAlpha: 0.18 };
                setResult(previewObj, keyForSymmetric(marginValue));
            } catch (e) {
                console.error('Symmetric preview error:', e);
                enqueueSnackbar(`Preview failed: ${getErrorMessage(e)}`, {
                    variant: 'error',
                });
            } finally {
                setIsPreviewing(false);
            }
        },
        [
            environment,
            inputRoi,
            beginOperation,
            setResult,
            cleanup,
            setIsPreviewing,
        ]
    );

    const handlePreviewAsymmetric = useCallback(
        async (negLpsMm: vec3, posLpsMm: vec3) => {
            if (
                !environment?.structureSet ||
                !inputRoi?.texture ||
                !environment.image
            ) {
                return;
            }

            if (vec3.length(negLpsMm) === 0 && vec3.length(posLpsMm) === 0) {
                cleanup();
                setIsPreviewing(false);
                return;
            }

            const expanded = getExpanded();
            if (!expanded) {
                return;
            }

            const signal = beginOperation();
            setIsPreviewing(true);

            // TODO: improve these kind of hacky rendering solutions should be removed and
            // heavy computation should be lifted away from blocking the main thread for smooth rendering
            await waitForUI();
            try {
                const marginTexture = await SDFOperations.applyAsymmetricMargin(
                    expanded,
                    negLpsMm,
                    posLpsMm
                );

                if (signal.aborted) {
                    marginTexture.delete();
                    return;
                }

                const previewObj = new CustomTargetObject(
                    environment.image,
                    marginTexture,
                    getPreviewColor(inputRoi.color)
                );
                previewObj.renderOptions = { fillAlpha: 0.18 };
                setResult(previewObj, keyForAsymmetric(negLpsMm, posLpsMm));
            } catch (e) {
                console.error('Asymmetric preview error:', e);
                cleanup();
                enqueueSnackbar(
                    `Margin preview failed: ${getErrorMessage(e)}`,
                    { variant: 'error' }
                );
            } finally {
                setIsPreviewing(false);
            }
        },
        [
            environment,
            inputRoi,
            getExpanded,
            beginOperation,
            setResult,
            cleanup,
            setIsPreviewing,
        ]
    );

    const handlePreviewIdentity = useCallback(() => {
        if (
            !environment?.structureSet ||
            !inputRoi?.texture ||
            !environment.image
        ) {
            return;
        }
        // All margins are zero: there is nothing to compute, but we still
        // "set the stage" so the other structures dim and the source is
        // highlighted in the preview colour — instead of showing no preview
        // and leaving every structure at full visibility. A clone of the
        // source SDF is used so disposing the preview never frees the
        // source's own texture.
        abortInFlight();
        const identityTexture = inputRoi.texture.clone();
        const previewObj = new CustomTargetObject(
            environment.image,
            identityTexture,
            getPreviewColor(inputRoi.color)
        );
        previewObj.renderOptions = { fillAlpha: 0.18 };
        setResult(previewObj, KEY_IDENTITY);
    }, [environment, inputRoi, abortInFlight, setResult]);

    const consumeSymmetricPreview = useCallback(
        (marginValueMm: number): ContourTexture | undefined =>
            consumeResult(keyForSymmetric(marginValueMm)),
        [consumeResult]
    );

    const consumeAsymmetricPreview = useCallback(
        (
            negLpsMm: ReadonlyVec3,
            posLpsMm: ReadonlyVec3
        ): ContourTexture | undefined =>
            consumeResult(keyForAsymmetric(negLpsMm, posLpsMm)),
        [consumeResult]
    );

    return useMemo(
        () => ({
            isPreviewing,
            handlePreviewSymmetric,
            handlePreviewAsymmetric,
            handlePreviewIdentity,
            consumeSymmetricPreview,
            consumeAsymmetricPreview,
            getExpanded,
            abortInFlight,
            cleanup,
            updateView,
        }),
        [
            isPreviewing,
            handlePreviewSymmetric,
            handlePreviewAsymmetric,
            handlePreviewIdentity,
            consumeSymmetricPreview,
            consumeAsymmetricPreview,
            getExpanded,
            abortInFlight,
            cleanup,
            updateView,
        ]
    );
}
